import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";

import SideBar from "../../../components/SideBar";
import BackButton from "../../../components/Base/BackButton";
import PageHeader from "../../../components/AdminCoachPanel/coaches/PageHeader/PageHeader";
import Pagination from "../../../components/AdminCoachPanel/coaches/Pagination/Pagination";
import DateRangePicker from "../../../components/AdminCoachPanel/coaches/DateRangePicker/DateRangePicker";
import {
  GetAllCoachMeetings,
  GetAllCoaches,
  GetCoachStudents,
} from "../../../api/AdminCoachPanel/coachApis";
import { Components } from "../../../assets/Dimensions";
import ExamCategoryType from "../../../enums/ExamCategoryType";

// Reuse existing styling: page wrapper, profile card/breadcrumb, and the exact
// same meetings table/modal styling as the coach profile "Toplantılar" tab.
import "../Coaches.css";
import "../../Coaches/CoachProfilePage.css";
import "../../Coaches/Components/meetingTabView/MeetingsTabView.css";
import "./AllCoachMeetings.css";

const PAGE_SIZE = 15;

// Invert ExamCategoryType ({ YKS: 1, LGS: 2, ... }) into an id -> name map so we
// can label coaches by their exam category. "None"/unknown ids get no label.
const EXAM_CATEGORY_LABEL = Object.keys(ExamCategoryType).reduce((acc, key) => {
  acc[ExamCategoryType[key]] = key;
  return acc;
}, {});

function examCategoryLabel(id) {
  const label = EXAM_CATEGORY_LABEL[id];
  return label && label !== "None" ? label : null;
}

// Local Date -> "YYYY-MM-DD" (query param the server filters on).
function toDateParam(d) {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Join a first/last name pair, treating the backend's "-" placeholder and
// blanks as empty; returns "-" when nothing meaningful remains.
function joinName(first, last) {
  const parts = [first, last].filter((x) => x && x !== "-");
  const joined = parts.join(" ").trim();
  return joined || "-";
}

// Map a raw backend meeting row (PascalCase) into the shape the table +
// recording modal render. `Date` looks like "2026-09-10T18:00:00" (no timezone,
// sometimes with fractional seconds), so we split it directly to show the
// backend's wall-clock time without any UTC drift.
//
// Only recordings that actually have a URL are kept: many come back with
// RecordUrl:null / Status:2 while still processing, and can't be played.
function mapMeeting(m) {
  const [datePart, timePart] = String(m.Date || "").split("T");
  return {
    id: m.MeetingId,
    date: datePart || "",
    time: timePart ? timePart.slice(0, 5) : "",
    coachName: joinName(m.CoachName, m.CoachSurname),
    studentName: joinName(m.StudentName, m.StudentSurname),
    recordings: (m.Recordings || [])
      .filter((r) => r.RecordUrl) // playable only
      .map((r) => ({
        Id: r.Id,
        RecordUrl: r.RecordUrl,
        Status: r.Status,
      })),
    title: m.Title,
  };
}

export default function AllCoachMeetings() {
  const history = useHistory();
  const userAdminToken = sessionStorage.getItem("jwtP");
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 700px)" });

  // Server-driven page state
  const [meetings, setMeetings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState(false);

  // Filters (all applied server-side).
  // selectedCoachId is the coach profile's Id (unique per exam-category profile)
  // and is what the meetings filter sends. selectedCoachUserId is that coach's
  // UserId, used only to fetch the coach's students.
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [selectedCoachUserId, setSelectedCoachUserId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [dateRange, setDateRange] = useState({ minDate: "", maxDate: "" });
  const [currentPage, setCurrentPage] = useState(1);
  // Bumped on reset to remount the (uncontrolled) date picker so it clears too.
  const [resetNonce, setResetNonce] = useState(0);

  // Filter dropdown option lists (fetched separately, not derived from a page)
  const [coachOptions, setCoachOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Recording modal state (same behaviour as MeetingsTabView)
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [activeRecordings, setActiveRecordings] = useState([]);
  const [activeRecordingUrl, setActiveRecordingUrl] = useState(null);

  // Guards against out-of-order responses clobbering fresh data.
  const meetingsReqRef = useRef(0);
  const studentsReqRef = useRef(0);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  /* ---- Coach options (once) --------------------------------------------- */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await GetAllCoaches(userAdminToken);
        if (!active) return;
        if (res && res.data && res.data.ResponseStatus === 1) {
          const opts = (res.data.ResponseData || [])
            // The same coach appears once per exam-category profile: same UserId,
            // but a distinct Id. The meetings filter is by the profile's Id, so
            // that's the option value (also unique, so no duplicate values). We
            // keep UserId separately for fetching that coach's students, and
            // append the exam category to the label — e.g. "Batuhan / YKS" — to
            // keep same-name entries distinguishable.
            .map((c) => {
              const name = joinName(c.Name, c.Surname);
              const cat = examCategoryLabel(c.ExamCategoryId);
              return {
                id: c.Id,
                userId: c.UserId,
                examCategoryId: c.ExamCategoryId,
                label: cat ? `${name} / ${cat}` : name,
              };
            })
            .filter((c) => c.id != null)
            .sort((a, b) =>
              a.label.localeCompare(b.label, "tr", { sensitivity: "base" })
            );
          setCoachOptions(opts);
        }
      } catch (err) {
        console.error("GetAllCoaches (coach options) error", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [userAdminToken]);

  /* ---- Student options (scoped to selected coach) ----------------------- */
  useEffect(() => {
    // Student filter only makes sense once a coach is chosen. Students are fetched
    // by the coach's UserId (not the profile Id).
    if (!selectedCoachUserId) {
      setStudentOptions([]);
      return;
    }
    const reqId = ++studentsReqRef.current;
    setStudentsLoading(true);
    (async () => {
      try {
        const res = await GetCoachStudents(userAdminToken, selectedCoachUserId);
        if (reqId !== studentsReqRef.current) return; // stale
        if (res && res.data && res.data.ResponseStatus === 1) {
          const opts = (res.data.ResponseData || [])
            .map((s) => ({
              id: s.UserId,
              name: (s.Name + " " + (s.Surname || "")).trim(),
            }))
            .filter((s) => s.id != null)
            .sort((a, b) =>
              a.name.localeCompare(b.name, "tr", { sensitivity: "base" })
            );
          setStudentOptions(opts);
        } else {
          setStudentOptions([]);
        }
      } catch (err) {
        console.error("GetCoachStudents (student options) error", err);
        setStudentOptions([]);
      } finally {
        if (reqId === studentsReqRef.current) setStudentsLoading(false);
      }
    })();
  }, [userAdminToken, selectedCoachUserId]);

  /* ---- Meetings page (refetch on page/filter change) -------------------- */
  useEffect(() => {
    const reqId = ++meetingsReqRef.current;
    setLoading(true);
    (async () => {
      try {
        // Send null (not "") for unset filters, matching the API contract.
        // The coach filter sends the coach profile's Id (selectedCoachId).
        const params = {
          pageNumber: currentPage,
          pageSize: PAGE_SIZE,
          coachUserId: selectedCoachId ? Number(selectedCoachId) : null,
          studentUserId: selectedStudentId ? Number(selectedStudentId) : null,
          minDate: toDateParam(dateRange.minDate) || null,
          maxDate: toDateParam(dateRange.maxDate) || null,
        };

        const res = await GetAllCoachMeetings(userAdminToken, params);
        if (reqId !== meetingsReqRef.current) return; // stale

        // The endpoint returns a flat PascalCase body { PageNumber, PageSize,
        // PageCount, TotalItemCount, Items }. Fall back to an enveloped
        // ResponseData / camelCase shape defensively.
        const body =
          res && res.data ? res.data.ResponseData || res.data : null;
        const rawItems = body
          ? Array.isArray(body.Items)
            ? body.Items
            : Array.isArray(body.items)
            ? body.items
            : null
          : null;

        if (rawItems) {
          const total = body.TotalItemCount != null ? body.TotalItemCount : body.totalItemCount;
          setMeetings(rawItems.map(mapMeeting));
          setTotalCount(typeof total === "number" ? total : rawItems.length);
        } else {
          toast.error("Toplantı kayıtları alınamadı.");
          setMeetings([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (reqId !== meetingsReqRef.current) return;
        console.error("GetAllCoachMeetings error", error);
        toast.error("Bir hata oluştu, lütfen tekrar deneyiniz.");
        setMeetings([]);
        setTotalCount(0);
      } finally {
        if (reqId === meetingsReqRef.current) setLoading(false);
      }
    })();
  }, [
    userAdminToken,
    currentPage,
    selectedCoachId,
    selectedStudentId,
    dateRange,
  ]);

  /* ---- Filter handlers (reset to page 1) -------------------------------- */
  function handleCoachChange(e) {
    const id = e.target.value;
    const opt = coachOptions.find((c) => String(c.id) === String(id));
    setSelectedCoachId(id);
    setSelectedCoachUserId(opt && opt.userId != null ? String(opt.userId) : "");
    setSelectedStudentId(""); // student list depends on coach
    setCurrentPage(1);
  }

  function handleStudentChange(e) {
    setSelectedStudentId(e.target.value);
    setCurrentPage(1);
  }

  function handleDateRangeChange({ minDate, maxDate }) {
    setDateRange({ minDate, maxDate });
    setCurrentPage(1);
  }

  const hasActiveFilters =
    !!selectedCoachId ||
    !!selectedStudentId ||
    !!dateRange.minDate ||
    !!dateRange.maxDate;

  function handleResetFilters() {
    setSelectedCoachId("");
    setSelectedCoachUserId("");
    setSelectedStudentId("");
    setDateRange({ minDate: "", maxDate: "" });
    setCurrentPage(1);
    setResetNonce((n) => n + 1); // remount date picker to clear its display
  }

  /* ---- Recording modal -------------------------------------------------- */
  function handleOpenRecording(recordings) {
    if (!recordings || recordings.length === 0) return;
    setActiveRecordings(recordings);
    setActiveRecordingUrl(recordings[0].RecordUrl);
    setIsVideoOpen(true);
  }

  function handleCloseRecording() {
    setIsVideoOpen(false);
    setActiveRecordings([]);
    setActiveRecordingUrl(null);
  }

  return (
    <>
      {isTabletOrMobile ? (
        <div>
          <span
            style={{ position: "absolute", top: 10, right: 10 }}
            onClick={() => setNav(!nav)}
          >
            menu
          </span>
          {nav && <SideBar />}
        </div>
      ) : (
        <SideBar />
      )}
      <ToastContainer autoClose={5000} />

      <div
        className="coaches-page"
        style={{
          paddingLeft: isTabletOrMobile ? 15 : Components.SideBarWidth + 15,
        }}
      >
        <div className="coaches-page-container">
          <BackButton />

          {/* Breadcrumb */}
          <div className="coach-profile-breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => history.push("/CoachesAdminPanel")}
            >
              Koçlar
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Tüm Toplantı Kayıtları</span>
          </div>

          <PageHeader title="Tüm Toplantı Kayıtları" />

          <div className="coach-profile-card">
            {/* Header + filters */}
            <div className="meetings-header">
              <h3>Tüm Koçların Toplantıları</h3>

              <div className="meetings-filters">
                {/* Coach filter */}
                <div className="meetings-filter-select-wrapper">
                  <select
                    className="meetings-filter-select"
                    value={selectedCoachId}
                    onChange={handleCoachChange}
                  >
                    <option value="">Tüm Koçlar</option>
                    {coachOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Student filter — scoped to the selected coach */}
                <div className="meetings-filter-select-wrapper">
                  <select
                    className="meetings-filter-select"
                    value={selectedStudentId}
                    onChange={handleStudentChange}
                    disabled={!selectedCoachId || studentsLoading}
                  >
                    <option value="">
                      {selectedCoachId
                        ? studentsLoading
                          ? "Yükleniyor…"
                          : "Tüm Öğrenciler"
                        : "Önce koç seçiniz"}
                    </option>
                    {studentOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <DateRangePicker
                  key={resetNonce}
                  onChangeDateRange={handleDateRangeChange}
                />

                <button
                  type="button"
                  className="meetings-reset-btn"
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters}
                >
                  Filtreleri Temizle
                </button>
              </div>
            </div>

            {/* Result count */}
            <div className="meetings-result-count">
              {loading ? "Yükleniyor…" : `${totalCount} toplantı`}
            </div>

            {/* Table */}
            <table className="meetings-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Koç</th>
                  <th>Öğrenci</th>
                  <th>Toplantı Başlığı</th>
                  <th>Aksiyonlar</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      Toplantılar yükleniyor…
                    </td>
                  </tr>
                ) : meetings.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      Toplantı kaydı bulunamadı
                    </td>
                  </tr>
                ) : (
                  meetings.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="meeting-date">
                          <div>
                            {new Date(m.date).toLocaleDateString("tr-TR", {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            })}
                          </div>
                          <div className="meeting-time">{m.time}</div>
                        </div>
                      </td>

                      <td className="meeting-coach">{m.coachName || "-"}</td>

                      <td>{m.studentName || "-"}</td>

                      <td>{m.title || "-"}</td>

                      <td className="meeting-actions">
                        <div className="recording-btn-wrapper">
                          <button
                            className="btn-recording active"
                            disabled={m.recordings.length === 0}
                            onClick={() => handleOpenRecording(m.recordings)}
                          >
                            Toplantı Kaydı
                          </button>

                          {m.recordings.length === 0 && (
                            <span className="recording-tooltip">
                              Toplantı kaydı yoktur
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Recording modal */}
            {isVideoOpen && (
              <div
                className="video-modal-backdrop"
                onClick={handleCloseRecording}
              >
                <div
                  className="video-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="video-modal-close"
                    onClick={handleCloseRecording}
                  >
                    ✕
                  </button>

                  <div className="video-modal-player">
                    <video
                      key={activeRecordingUrl}
                      src={activeRecordingUrl}
                      controls
                      autoPlay
                    />
                  </div>

                  {activeRecordings.length > 1 && (
                    <div className="video-modal-list">
                      {activeRecordings.map(function (r, index) {
                        const isActive = r.RecordUrl === activeRecordingUrl;
                        return (
                          <div
                            key={r.Id}
                            className={
                              "video-modal-list-item " +
                              (isActive ? "active" : "")
                            }
                            onClick={() => setActiveRecordingUrl(r.RecordUrl)}
                          >
                            <span>Kayıt {index + 1}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={PAGE_SIZE}
              totalItems={totalCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
}
